<template>
    <div style="display: flex; justify-content: space-between">
        <Title>Domain Objects</Title>
        <div style="display: flex; gap: 0.5rem; align-items: center">
            <Button
                v-if="isEditing"
                label="Add domain object"
                icon="pi pi-plus"
                text
                size="small"
                severity="secondary"
                @click="openEntityDialog()"
            />
        </div>
    </div>
    <p class="section-description">
        The key data objects your domain works with — what your processes
        create, read, update, and transform.
    </p>
    <div v-if="!entities?.length" class="empty-state">
        <i class="pi pi-building empty-icon"></i>
        <div>
            <h3>No domain objects yet</h3>
            <p>
                <i
                    >Add the key data objects your domain works with so
                    processes can reference them.</i
                >
            </p>
            <Button
                v-if="isEditing"
                label="Add domain object"
                icon="pi pi-plus"
                severity="primary"
                text
                @click="openEntityDialog()"
            />
        </div>
    </div>
    <DataTable
        ref="dataTable"
        v-else
        :loading="isLoading"
        :value="entities"
        selection-mode="single"
        dataKey="identity"
        :rowHover="true"
        :rowEditor="isEditing"
        @row-click="
            (event: DataTableRowClickEvent) => {
                const rowLink = `entity-link-${event.data.identity}`;
                dataTable.$el.querySelector(`#${rowLink}`)?.click();
            }
        "
    >
        <template #loading> Fetching data... </template>
        <Column field="name" header="Name" class="name-col">
            <template #body="{ data, field }">
                <RouterLink
                    v-if="hasValidRouteParams"
                    :id="`entity-link-${data.identity}`"
                    class="entity-link"
                    :to="getEntityRoute(data.identity)"
                >
                    <div>
                        {{ data[field] }}
                    </div>
                </RouterLink>
                <div v-else :id="`entity-link-${data.identity}`">
                    {{ data[field] }}
                </div>
            </template>
        </Column>
        <Column field="short_description" header="Description">
            <template #body="slotProps">
                <template v-if="slotProps.data.short_description">
                    {{ slotProps.data.short_description }}
                </template>
                <span
                    v-else
                    v-tooltip.top="'A description is not (yet) available'"
                >
                    ...
                </span>
            </template>
        </Column>
        <Column header="Attributes" class="count-col">
            <template #body="{ data }">
                <span class="count-badge">{{ data.attribute_count ?? 0 }}</span>
            </template>
        </Column>
        <Column header="Relationships" class="count-col">
            <template #body="{ data }">
                <span class="count-badge">{{ data.relationships.length }}</span>
            </template>
        </Column>
        <Column field="" header="" class="buttons-col">
            <template #body="{ index }" v-if="isEditing">
                <Button
                    v-tooltip.top="'Delete domain object'"
                    severity="danger"
                    type="button"
                    text
                    rounded
                    icon="pi pi-times"
                    :loading="isLoading"
                    @click="
                        (e: MouseEvent) => {
                            // Using stopPropagation will break confirm: https://github.com/primefaces/primevue/issues/5706
                            deleteBusinessEntity(e, index);
                        }
                    "
                />
            </template>
        </Column>
    </DataTable>
    <EntitiesDiagram v-if="entities?.length" :entities="entities" />
</template>

<script setup lang="ts">
import { DataTableRowClickEvent } from "primevue/datatable";
import { useConfirm } from "primevue/useconfirm";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";

import Title from "@/components/Title.vue";
import EntitiesDiagram from "@/components/entities/EntitiesDiagram.vue";
import { useEditEntityDialog } from "@/composables/edit-entity-dialog";
import {
    BusinessEntity,
    useBusinessEntitiesManager,
    useBusinessEntitiesStore,
} from "@/stores/business-entities-store";

const props = defineProps<{
    isEditing: boolean;
    entities: BusinessEntity[];
}>();

const dataTable = ref();
const route = useRoute();

const hasValidRouteParams = computed(() =>
    Boolean(route.params.orgName && route.params.stekName)
);

const getEntityRoute = (entityId: string) => {
    if (!hasValidRouteParams.value) return undefined;
    return {
        name: "businessEntityPage",
        params: {
            orgName: route.params.orgName,
            stekName: route.params.stekName,
            businessEntityId: entityId,
        },
    };
};

const { isLoading } = useBusinessEntitiesManager();
const { deleteEntity } = useBusinessEntitiesStore();
const { open: openEntityDialog } = useEditEntityDialog();

const confirm = useConfirm();

const deleteBusinessEntity = async (event: Event, index: number) => {
    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: `
            After deletion of '${props.entities[index].name}'
            as a domain object, you cannot reference it anymore. Are you sure?
        `,
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Delete",
            severity: "danger",
        },
        accept: async () => {
            await deleteEntity(props.entities[index].identity);
        },
    });
};
</script>

<style scoped lang="scss">
.section-description {
    margin: 0.25rem 0 1rem;
    color: var(--p-text-muted-color);
    font-size: 0.875rem;
    font-style: italic;
}

.entity-link {
    text-decoration: none;
}

:deep(.p-datatable-thead) > tr > th,
:deep(.p-datatable-tbody) > tr > td {
    &.buttons-col {
        min-width: 50px;
        text-align: right;
    }
    &.name-col {
        width: 30rem;
    }
    &.count-col {
        width: 8rem;
        text-align: center;
    }
}

.count-badge {
    display: inline-block;
    min-width: 1.5rem;
    text-align: center;
    font-size: 0.8rem;
    color: var(--p-text-muted-color);
}
:deep(.p-datatable-tbody) > tr > td.buttons-col {
    padding-top: 0;
    padding-bottom: 0;
}

.empty-state {
    display: flex;
    flex-direction: column;
    gap: 1em;
    align-items: center;
    text-align: center;

    > * {
        margin: 0;
    }

    .empty-icon {
        font-size: 3rem;
    }
}
</style>
